"use client";

import Image from "./image";
import Link from "./router-link";
import { ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Stagger, StaggerItem } from "./motion";

type SolutionItem = {
  slug: string;
  number: string;
  title: string;
  description: string;
  image: string;
  tags: string[];
  details: string[];
};

export function SolutionTabs({ items }: { items: SolutionItem[] }) {
  const { hash } = useLocation();
  const [activeIndex, setActiveIndex] = useState(0);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeItem = items[activeIndex];

  useEffect(() => {
    const index = items.findIndex((item) => item.slug === hash.slice(1));
    if (index < 0) return;

    setActiveIndex(index);
    requestAnimationFrame(() => {
      tabsRef.current?.scrollIntoView({ block: "start" });
    });
  }, [hash, items]);

  const selectTab = (index: number) => {
    setActiveIndex(index);
    window.history.replaceState(null, "", `#${items[index].slug}`);
  };

  const moveFocus = (index: number) => {
    selectTab(index);
    tabRefs.current[index]?.focus();
  };

  return (
    <div className="solution-tabs" ref={tabsRef}>
      <div aria-label="增长方案" className="solution-tab-scroll" role="tablist">
        <Stagger className="solution-tab-list">
          {items.map((item, index) => (
            <StaggerItem className="solution-tab-item" key={item.number}>
              <button
                aria-controls={`solution-panel-${item.number}`}
                aria-selected={activeIndex === index}
                className="solution-tab"
                id={item.slug}
                onClick={() => selectTab(index)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowRight") {
                    event.preventDefault();
                    moveFocus((index + 1) % items.length);
                  }
                  if (event.key === "ArrowLeft") {
                    event.preventDefault();
                    moveFocus((index - 1 + items.length) % items.length);
                  }
                  if (event.key === "Home") {
                    event.preventDefault();
                    moveFocus(0);
                  }
                  if (event.key === "End") {
                    event.preventDefault();
                    moveFocus(items.length - 1);
                  }
                }}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                role="tab"
                tabIndex={activeIndex === index ? 0 : -1}
                type="button"
              >
                <span>{item.number}</span>
                <strong>{item.title}</strong>
                {activeIndex === index && (
                  <motion.span
                    className="solution-tab-indicator"
                    layoutId="solution-tab-indicator"
                  />
                )}
              </button>
            </StaggerItem>
          ))}
        </Stagger>
      </div>

      <AnimatePresence initial={false} mode="wait">
        <motion.article
          animate={{ opacity: 1, x: 0 }}
          aria-labelledby={activeItem.slug}
          className="solution-tab-panel"
          exit={{ opacity: 0, x: -18 }}
          id={`solution-panel-${activeItem.number}`}
          initial={{ opacity: 0, x: 24 }}
          key={activeItem.number}
          role="tabpanel"
          transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="solution-detail-visual">
            <motion.div
              animate={{ clipPath: "inset(0 0% 0 0)" }}
              className="solution-detail-image"
              exit={{ clipPath: "inset(0 0 0 100%)" }}
              initial={{ clipPath: "inset(0 100% 0 0)" }}
              transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            >
              <Image
                src={activeItem.image}
                alt={activeItem.title}
                fill
                sizes="(max-width: 900px) 100vw, 38vw"
              />
            </motion.div>
            <div className="solution-detail-title">
              <span>{activeItem.number} / 增长路径</span>
              <h2>{activeItem.title}</h2>
            </div>
          </div>

          <div className="solution-detail-content">
            <div className="solution-detail-intro">
              <span>方案概览</span>
              <p>{activeItem.description}</p>
            </div>
            <div className="solution-detail-list">
              {activeItem.tags.map((tag, index) => (
                <div className="solution-detail-row" key={tag}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <strong>{tag}</strong>
                  <p>{activeItem.details[index]}</p>
                </div>
              ))}
            </div>
            <Link className="text-link" href={`/solutions/${activeItem.slug}`}>
              查看完整方案 <ArrowUpRight size={16} />
            </Link>
          </div>
        </motion.article>
      </AnimatePresence>
    </div>
  );
}
